/**
 * Echipa: membri, roluri, invitații.
 *
 * Interfața ascunde acțiunile pe care rolul curent nu le are (nu le „dezactivează" — le ascunde),
 * dar serverul rămâne singurul care decide: anti-escaladarea (`canGrant`) și protecția ultimului
 * proprietar sunt verificate acolo, indiferent de ce trimite clientul.
 */
import { useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { formatDateTime, relativeTime, useResource } from '../lib/hooks.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';
import { useToast } from '../components/ui.js';

interface Member {
  id: string;
  name: string;
  email: string;
  role_id: string;
  role_code: string;
  role_name: string;
  status: string;
  created_at: string;
  accepted_at: string | null;
  last_login_at: string | null;
  is_self: boolean;
}

interface Role {
  id: string;
  code: string;
  name: string;
  is_system: boolean;
  scope_level: string;
  permissions: string[];
}

interface Invite {
  id: string;
  email: string;
  role_name: string;
  expires_at: string;
  created_at: string;
  status: string;
}

export function TeamPage() {
  const session = useSession();
  const toast = useToast();
  const members = useResource<Member[]>(() => api.get<Member[]>('/members'), [session.activeAccountId]);
  const roles = useResource<Role[]>(() => api.get<Role[]>('/roles'), [session.activeAccountId]);
  const invites = useResource<Invite[]>(() => api.get<Invite[]>('/invites'), [session.activeAccountId]);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', roleId: '' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
  const [roleTarget, setRoleTarget] = useState<Member | null>(null);

  const canInvite = session.can('team.invite');
  const canUpdate = session.can('team.update');

  const assignableRoles = (roles.data ?? []).filter(
    (role) => role.code !== 'owner' || session.role === 'owner' || session.user?.isPlatformAdmin,
  );

  async function sendInvite() {
    setBusy(true);
    setErrors({});
    setFormError(null);
    const roleId = inviteForm.roleId || assignableRoles.find((role) => role.code === 'editor')?.id;
    if (!roleId) {
      setFormError('Alege un rol pentru invitație.');
      setBusy(false);
      return;
    }
    try {
      await api.post('/invites', { email: inviteForm.email.trim().toLowerCase(), roleId });
      toast.success('Invitație trimisă', 'Persoana primește un email cu link de acceptare, valabil 7 zile.');
      setInviteOpen(false);
      setInviteForm({ email: '', roleId: '' });
      await invites.reload();
    } catch (error) {
      setErrors(fieldErrors(error));
      setFormError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function revokeInvite(id: string, email: string) {
    try {
      await api.del(`/invites/${id}`);
      toast.success('Invitație revocată', `Linkul trimis către ${email} nu mai funcționează.`);
      await invites.reload();
    } catch (error) {
      toast.error('Nu am putut revoca invitația', errorMessage(error));
    }
  }

  async function changeRole(member: Member, roleId: string) {
    try {
      await api.patch(`/members/${member.id}`, { roleId });
      toast.success('Rol actualizat', `${member.name} are acum rolul „${roles.data?.find((r) => r.id === roleId)?.name}”.`);
      setRoleTarget(null);
      await members.reload();
    } catch (error) {
      toast.error('Nu am putut schimba rolul', errorMessage(error));
    }
  }

  async function removeMember() {
    if (!removeTarget) return;
    setBusy(true);
    try {
      await api.del(`/members/${removeTarget.id}`);
      toast.success('Membru eliminat', `${removeTarget.name} nu mai are acces la cont.`);
      setRemoveTarget(null);
      await members.reload();
    } catch (error) {
      toast.error('Nu am putut elimina membrul', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-enter">
      <PageHeader
        title="Echipă"
        subtitle="Roluri clare, permisiuni minime, audit complet al acțiunilor."
        actions={
          canInvite ? (
            <Button icon={<Icon name="plus" size={17} />} onClick={() => setInviteOpen(true)}>
              Invită membru
            </Button>
          ) : null
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <Card>
          <h3 className="mb-4 font-display text-title text-ink-950">Membri</h3>
          {members.loading ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-12" />
              ))}
            </div>
          ) : !members.data?.length ? (
            <EmptyState title="Doar tu în cont" description="Invită colegii și atribuie-le roluri potrivite." />
          ) : (
            <ul className="flex flex-col divide-y divide-ink-100">
              {members.data.map((member) => (
                <li key={member.id} className="flex flex-wrap items-center gap-3 py-3">
                  <Avatar name={member.name} size={38} />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="flex items-center gap-2 truncate text-body-s font-medium text-ink-950">
                      {member.name}
                      {member.is_self ? <Badge tone="neutral">tu</Badge> : null}
                    </span>
                    <span className="truncate text-caption text-ink-400">{member.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={member.role_code === 'owner' ? 'brand' : 'neutral'}>{member.role_name}</Badge>
                    {member.last_login_at ? (
                      <span className="hidden text-caption text-ink-400 sm:inline">
                        ultima autentificare {relativeTime(member.last_login_at)}
                      </span>
                    ) : (
                      <span className="hidden text-caption text-ink-400 sm:inline">fără autentificări</span>
                    )}
                  </div>
                  {canUpdate && !member.is_self ? (
                    <div className="flex gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => setRoleTarget(member)}>
                        Schimbă rol
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setRemoveTarget(member)}>
                        Elimină
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="flex flex-col gap-6">
          {invites.data?.length ? (
            <Card>
              <h3 className="mb-4 font-display text-title text-ink-950">Invitații în așteptare</h3>
              <ul className="flex flex-col divide-y divide-ink-100">
                {invites.data.map((invite) => (
                  <li key={invite.id} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-body-s text-ink-950">{invite.email}</span>
                      <span className="text-caption text-ink-400">
                        {invite.role_name} · expiră {formatDateTime(invite.expires_at)}
                      </span>
                    </div>
                    {canInvite ? (
                      <Button variant="ghost" size="sm" onClick={() => void revokeInvite(invite.id, invite.email)}>
                        Revocă
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <Card>
            <h3 className="mb-4 font-display text-title text-ink-950">Roluri disponibile</h3>
            {roles.loading ? (
              <div className="flex flex-col gap-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-10" />
                ))}
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {(roles.data ?? []).map((role) => (
                  <li key={role.id} className="rounded-[var(--radius-sm)] bg-surface-3/50 px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-body-s font-medium text-ink-950">{role.name}</span>
                      <span className="text-caption text-ink-400">{role.permissions.length} permisiuni</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-caption text-ink-400">
              Permisiunile se verifică pe server la fiecare cerere — interfața doar le reflectă.
            </p>
          </Card>
        </div>
      </div>

      {/* Invitație */}
      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invită un membru"
        description="Primește un email cu link de acceptare, valabil 7 zile."
        footer={
          <>
            <Button variant="secondary" onClick={() => setInviteOpen(false)} disabled={busy}>
              Anulează
            </Button>
            <Button loading={busy} onClick={() => void sendInvite()}>
              Trimite invitația
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}
          <Field label="Email" required errors={errors.email}>
            {(props) => (
              <Input
                {...props}
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((current) => ({ ...current, email: e.target.value }))}
                placeholder="coleg@restaurant.ro"
              />
            )}
          </Field>
          <Field label="Rol" errors={errors.roleId} hint="Rolul poate fi schimbat oricând.">
            {(props) => (
              <Select
                {...props}
                value={inviteForm.roleId || assignableRoles.find((role) => role.code === 'editor')?.id || ''}
                onChange={(e) => setInviteForm((current) => ({ ...current, roleId: e.target.value }))}
              >
                {assignableRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name} — {role.permissions.length} permisiuni
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Modal>

      {/* Schimbare rol */}
      <Modal
        open={Boolean(roleTarget)}
        onClose={() => setRoleTarget(null)}
        title={`Rol pentru ${roleTarget?.name ?? ''}`}
        size="sm"
      >
        <ul className="flex flex-col gap-2">
          {assignableRoles.map((role) => (
            <li key={role.id}>
              <button
                type="button"
                onClick={() => roleTarget && void changeRole(roleTarget, role.id)}
                className="transition-ui flex w-full items-center justify-between rounded-[var(--radius-sm)] border border-ink-100 px-3 py-3 text-left hover:border-brand-100 hover:bg-brand-100/30"
              >
                <span className="flex flex-col">
                  <span className="text-body-s font-medium text-ink-950">{role.name}</span>
                  <span className="text-caption text-ink-400">{role.permissions.length} permisiuni</span>
                </span>
                {role.id === roleTarget?.role_id ? <Icon name="check" size={17} className="text-brand-600" /> : null}
              </button>
            </li>
          ))}
        </ul>
      </Modal>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => void removeMember()}
        loading={busy}
        title={`Elimini pe ${removeTarget?.name ?? ''}?`}
        description="Persoana pierde imediat accesul la acest cont. Acțiunea apare în audit."
        confirmLabel="Elimină din echipă"
      />
    </div>
  );
}
